// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { css } from '@emotion/css';
import { Typography, Theme, useTheme } from '@mui/material';

// TYPES ---------------------------------------------------------------- //

interface ChartCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  minHeight?: number;
  empty?: boolean;
  emptyMessage?: string;
}

// COMPONENT ---------------------------------------------------------------- //

const ChartCard = ({
  title,
  subtitle,
  children,
  minHeight = 280,
  empty = false,
  emptyMessage = 'No move data for this date range.',
}: ChartCardProps) => {
  const theme = useTheme();
  const cls = useStyles(theme, minHeight);

  return (
    <div className={cls.card}>
      <div className={cls.header}>
        <Typography className={cls.title}>{title}</Typography>
        {subtitle ? <Typography className={cls.subtitle}>{subtitle}</Typography> : null}
      </div>

      <div className={cls.body}>
        {empty ? <Typography className={cls.emptyTxt}>{emptyMessage}</Typography> : children}
      </div>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme, minHeight?: number) => {
  return {
    card: css`
      position: relative;
      display: flex;
      flex-direction: column;
      height: 100%;
      padding: ${theme?.spacing(2)};
      border: 1px solid ${theme?.palette?.divider};
      border-radius: 10px;
      background-color: ${theme?.palette?.background?.paper};
    `,
    header: css`
      margin-bottom: ${theme?.spacing(1.5)};
    `,
    title: css`
      font-size: 15px !important;
      font-weight: 600 !important;
      color: ${theme?.palette?.text?.primary};
    `,
    subtitle: css`
      font-size: 12px !important;
      color: ${theme?.palette?.text?.secondary};
    `,
    body: css`
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      min-height: ${minHeight || 280}px;
    `,
    emptyTxt: css`
      font-size: 13px !important;
      color: ${theme?.palette?.text?.disabled};
    `,
  };
};

// EXPORT ---------------------------------------------------------------- //

export default ChartCard;
